import * as React from "react";

import { Button, IconButton, Input } from "Components/ui";
import { Plus, Tag, Trash2 } from "Components/ui/icons";
import { cn } from "utils/cn";
import { formatINR } from "utils/format";
import { LineItemDraft } from "../types";
import { lineTotalPaise, MAX_LINE_ITEMS, newLineItem } from "../lineItems";

interface LineItemsEditorProps {
  items: LineItemDraft[];
  onChange: (items: LineItemDraft[]) => void;
  errors: Record<string, string>;
  total: number;
  totalError: string | null;
  showErrors: boolean;
}

/** Description · qty · ₹ amount rows with a running total; discount lines are subtracted. */
const LineItemsEditor: React.FC<LineItemsEditorProps> = ({
  items,
  onChange,
  errors,
  total,
  totalError,
  showErrors,
}) => {
  const update = (key: string, patch: Partial<LineItemDraft>) =>
    onChange(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  const remove = (key: string) => onChange(items.filter((item) => item.key !== key));
  const add = (kind: LineItemDraft["kind"]) => onChange([...items, newLineItem(kind)]);
  const atLimit = items.length >= MAX_LINE_ITEMS;

  return (
    <div className="space-y-2">
      <div className="hidden grid-cols-12 gap-2 px-2 text-xs font-medium text-muted-foreground sm:grid">
        <span className="col-span-5">Description</span>
        <span className="col-span-2">Qty</span>
        <span className="col-span-3">Amount each (₹)</span>
        <span className="col-span-2 pr-10 text-right">Line total</span>
      </div>
      {items.map((item, index) => {
        const lineTotal = lineTotalPaise(item);
        const error = showErrors ? errors[item.key] : undefined;
        const isDiscount = item.kind === "discount";
        const errorId = `line-item-${item.key}-error`;
        return (
          <div
            key={item.key}
            className={cn(
              "rounded-control border p-2",
              isDiscount
                ? "border-success/30 bg-success-subtle"
                : "border-border bg-surface-muted/60"
            )}
          >
            <div className="grid grid-cols-12 items-center gap-2">
              <Input
                type="text"
                value={item.description}
                onChange={(event) => update(item.key, { description: event.target.value })}
                placeholder={isDiscount ? "Discount" : "e.g. TeleOpd subscription"}
                aria-label={`Line ${index + 1} description`}
                aria-invalid={Boolean(error) && !item.description.trim() ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                maxLength={200}
                wrapperClassName="col-span-12 sm:col-span-5"
              />
              <Input
                type="text"
                inputMode="numeric"
                value={item.qty}
                onChange={(event) => update(item.key, { qty: event.target.value })}
                aria-label={`Line ${index + 1} quantity`}
                wrapperClassName="col-span-3 sm:col-span-2"
                className="text-right tabular-nums"
              />
              <Input
                type="text"
                inputMode="decimal"
                value={item.amount}
                onChange={(event) => update(item.key, { amount: event.target.value })}
                placeholder="0"
                aria-label={`Line ${index + 1} amount in rupees`}
                leading={<span className="text-sm">{isDiscount ? "−₹" : "₹"}</span>}
                wrapperClassName="col-span-5 sm:col-span-3"
                className="text-right tabular-nums"
              />
              <div className="col-span-3 text-right text-sm tabular-nums sm:col-span-1">
                <span className={isDiscount ? "text-success-text" : "text-foreground"}>
                  {lineTotal === null ? "—" : formatINR(lineTotal)}
                </span>
              </div>
              <div className="col-span-1 flex justify-end">
                <IconButton
                  label={`Remove line ${index + 1}`}
                  icon={<Trash2 aria-hidden="true" />}
                  size="sm"
                  onClick={() => remove(item.key)}
                  disabled={items.length <= 1}
                />
              </div>
            </div>
            {error && (
              <p id={errorId} className="mt-1 px-1 text-xs text-danger-text">
                {error}
              </p>
            )}
          </div>
        );
      })}
      <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => add("charge")}
            disabled={atLimit}
            leftIcon={<Plus aria-hidden="true" />}
          >
            Add line
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => add("discount")}
            disabled={atLimit}
            leftIcon={<Tag aria-hidden="true" />}
          >
            Add discount
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Total{" "}
          <span className="ml-1 text-lg font-semibold tabular-nums text-foreground">
            {formatINR(total)}
          </span>
        </p>
      </div>
      {showErrors && totalError && <p className="text-xs text-danger-text">{totalError}</p>}
    </div>
  );
};

export default LineItemsEditor;
